import { useState } from "react";
import { useTranslation } from "react-i18next";

import CreateRecordButtonLayout from "../layout/CreateRecordButtonLayout";
import CompanyDialog from "../dialogTypes/CompanyDialog";

/**
 * @COMPONENT_NAME CompanyCreateButton
 * @DESCRIPTION 새로운 회사 레코드 생성을 위한 버튼 컴포넌트. 회사 생성 폼이 포함된 다이얼로그를 엽니다.
 *
 * @PROPS
 *    - {string} id - 버튼 식별자
 *    - {Object} schema - 폼 스키마 구성
 *    - {Object} refresh - 그리드 새로고침 함수
 *    - {Function} onClose - 다이얼로그 닫힐 때 호출
 *    - {Function} onSuccess - 생성 성공 시 호출
 *    - {string} createButtonName - 커스텀 버튼명
 *    - {Function} customizeErrorMsgCallback - 커스텀 오류 메시지 콜백
 *    - {Function} setInitialDataCallback - 초기 데이터 설정 콜백
 *    - {Function} modifyUrlCallback - URL 수정 콜백
 *
 * @PASSTHROUGH_PROPS
 *    - {Object} others - 버튼 레이아웃에 전달되는 추가 props
 *
 * @CALLBACKS
 *    - {Function} onOpen - 버튼 클릭 시 호출
 *    - {Function} onDialogClose - 다이얼로그 닫힐 때 호출
 *    - {Function} onCreateSuccess - 생성 성공 시 호출
 *
 * @EXAMPLES
 * ```jsx
 * <CompanyCreateButton
 *   id="company-create"
 *   schema={schema}
 *   refresh={refresh}
 *   onSuccess={handleSuccess}
 * />
 * ```
 */

function CompanyCreateButton(props) {
    const { t } = useTranslation();

    const {
        id,
        schema,
        refresh,
        onClose,
        onSuccess,
        createButtonName,
        customizeErrorMsgCallback,
        setInitialDataCallback,
        modifyUrlCallback,
        ...others
    } = props;
    const [open, setOpen] = useState(false);

    const onOpen = () => {
        setOpen(true);
    };

    const onDialogClose = () => {
        setOpen(false);

        if (onClose) {
            onClose();
        }
    };

    const onCreateSuccess = (msg) => {
        if (onSuccess) {
            onSuccess(msg);
        }

        if (refresh) {
            refresh.refresh();
        }
    };

    return (
        <>
            <CreateRecordButtonLayout
                id={id + "create-button"}
                onClick={onOpen}
                createButtonName={createButtonName}
                {...others}
            />
            <CompanyDialog
                id={id + "create-dialog"}
                mode="create"
                method="post"
                formId={id + "create-form"}
                title={schema ? t(`dialog.title.MomSysUdtCompany.create`) : ""}
                schema={schema}
                open={open}
                onClose={onDialogClose}
                onSuccess={onCreateSuccess}
            />
        </>
    );
}

export default CompanyCreateButton;
